import { useState } from 'react';
import { useSelector } from 'react-redux';
import { BUSINESS_TYPE_CATEGORY } from 'constants/companyTypes';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  CollapsibleCard,
  CollapsibleCardGroup,
  CollapsibleCardGroupControls,
} from './collapsible-card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  DollarSign,
  ShoppingCart,
  BarChart3,
  Users,
  Clock,
  Download,
  ChevronRight,
  X,
} from 'lucide-react';
import { VirtualizedOrderTable } from './virtualized-order-table';
import { isReportSectionEnabled, isTenderTypeShown } from './shared/schema';
import {
  SourceRow,
  SourceBreakdownList,
  sumSources,
  indexDailySources,
} from './source-breakdown';

// Transform a flat detailedReport order into the nested structure
// VirtualizedOrderTable expects. Used for the page on screen and for the rows
// the CSV export fetches separately.
function toDetailedOrderRow(item) {
  return {
    dateOnly: item.dateOnly,
    timeOnly: item.timeOnly,
    rewardsInfo: {
      usedRewardsAmount:
        (item.rewardsAmount || 0) +
        (item.totalPurchaseRewardsAmount || 0) -
        (item.totalRefundRewardsAmount || 0),
    },
    order: {
      displayNumber: item.displayNumber || item.orderNumber || 'N/A',
      placedAt: item.placedAt || item.actualPlacedDate,
      orderType: item.orderType || 'unknown',
      provider: item.provider || 'pos',
      charges: {
        subTotal: item.subtotal || 0,
        taxAmount: item.tax || 0,
        deliveryFee: item.deliveryFee || 0,
        deliveryCost: item.deliveryCost || 0,
        deliveryCostPaid: item.deliveryCostPaid || 0,
        originalDeliveryFee: item.originalDeliveryFee || 0,
        delivery: item.delivery || 0,
        tipInfo: {
          kitchen: item.kitchenTip || 0,
          driver: item.driverTip || 0,
          driverPaid: item.driverTipPaid || 0,
          originalDriver: item.originalDriverTip || 0,
          originalKitchen: item.originalKitchenTip || 0,
          driverCost: item.driverTipCost || 0,
        },
        discount: item.discount || 0,
        total: item.total || 0,
        balanceDue: item.balanceDue || 0,
        regularDiscountTotal: item.orderDiscountTotal || 0,
        cashDiscountTotal: item.cashDiscountTotal || 0,
        couponTotal: item.couponTotal || 0,
        promotionTotal: item.promotionTotal || 0,
        rewardAmount: item.rewardAmount || 0,
        refundAmount: item.totalRefundAmount || 0,
        tip: (item.kitchenTip || 0) + (item.driverTip || 0),
        amountCollected:
          (item.totalPurchaseAmount || 0) - (item.totalRefundAmount || 0),
        adjustmentsAndFees: item.adjustmentsAndFees || 0,
        surchargeAmount: item.surchargeAmount || 0,
      },
      employeeInfo: item.employeeInfo || {},
    },
  };
}

export default function PosPerformanceOutput({
  data,
  isDeliveryReport = false,
  include,
  tenderTypes,
  onOrdersPageChange,
  onExportAllOrders,
  isOrdersPageLoading = false,
}) {
  const { currentBusiness } = useSelector(({ business }) => business);
  const isInventoryStore = currentBusiness?.businessTypes?.includes(
    BUSINESS_TYPE_CATEGORY.inventoryStore
  );

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showTotalProfitReportDetail, setShowTotalProfitReportDetail] =
    useState(false);
  console.log('📊 data.tipBreakdown:', data.tipBreakdown);
  console.log('👥 data.employeeTimesheetReport:', data.employeeTimesheetReport);
  console.log(
    '💼 data.employeeDetailedBreakdown:',
    data.employeeDetailedBreakdown
  );

  const formatCurrency = (amount) => {
    const normalized = Object.is(amount, -0) ? 0 : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(normalized);
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      console.warn('⚠️ formatDate called with empty dateString:', {
        value: dateString,
        type: typeof dateString,
        isNull: dateString === null,
        isUndefined: dateString === undefined,
      });
      return 'N/A';
    }

    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
  };

  const getDayOfWeek = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const formatDateTime = (dateString) => {
    const date = dateString ? new Date(dateString) : new Date();
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Format report period: MM/DD/YYYY, HH:MM:SS AM/PM
  const formatReportPeriod = () => {
    if (!data.dateRange) return 'N/A';

    const formatDateTimeForPeriod = (dateStr, timeStr = '00:00') => {
      if (!dateStr) return 'N/A';

      // Parse date (YYYY-MM-DD)
      const [year, month, day] = dateStr.split('-').map(Number);

      // Parse time (HH:MM)
      const [hours, minutes] = timeStr.split(':').map(Number);
      const seconds = 0; // Form only provides HH:MM, so seconds default to 0

      // Create date object
      const date = new Date(year, month - 1, day, hours, minutes, seconds);

      // Format as MM/DD/YYYY, HH:MM:SS AM/PM (no leading zero on hour)
      const monthStr = String(date.getMonth() + 1).padStart(2, '0');
      const dayStr = String(date.getDate()).padStart(2, '0');
      const yearStr = date.getFullYear();

      let hour12 = date.getHours();
      const ampm = hour12 >= 12 ? 'PM' : 'AM';
      hour12 = hour12 % 12 || 12;
      // No padding for hour - keep as is (1-12)
      const hourStr = String(hour12);
      const minStr = String(date.getMinutes()).padStart(2, '0');
      const secStr = String(date.getSeconds()).padStart(2, '0');

      return `${monthStr}/${dayStr}/${yearStr}, ${hourStr}:${minStr}:${secStr} ${ampm}`;
    };

    const startDateTime = formatDateTimeForPeriod(
      data.dateRange.startDate,
      data.dateRange.startTime || '00:00'
    );
    const endDateTime = formatDateTimeForPeriod(
      data.dateRange.endDate,
      data.dateRange.endTime || '23:59'
    );

    return `${startDateTime} - ${endDateTime}`;
  };

  const reportPeriod = formatReportPeriod();
  const hasDriverBreakdown = Boolean(data.driverBreakdown?.breakdown?.length);
  const showDriverBreakdown =
    isDeliveryReport &&
    isReportSectionEnabled(include, 'driverBreakdown', hasDriverBreakdown);
  const hasDailyBreakdown = Boolean(data.dailyBreakdown?.length);
  const showDailyBreakdown = isReportSectionEnabled(
    include,
    'dailyBreakdown',
    hasDailyBreakdown
  );

  // Escape a single CSV cell
  const escapeCsvCell = (cell) => {
    const cellStr = String(cell ?? '');
    if (
      cellStr.includes(',') ||
      cellStr.includes('"') ||
      cellStr.includes('\n')
    ) {
      return `"${cellStr.replace(/"/g, '""')}"`;
    }
    return cellStr;
  };

  const toCsvRow = (cells) => cells.map(escapeCsvCell).join(',');

  // Build the CSV lines for a single driver, mirroring exactly what is shown
  // on screen (same sections, labels, order and currency-formatted values).
  const buildDriverCsvBlock = (driver) => {
    const lines = [];

    // Driver header — matches the on-screen header (name, phone, email, orders)
    lines.push(
      toCsvRow(['Driver Name', driver.driverName || 'Unknown Driver'])
    );
    if (driver.driverPhone) {
      lines.push(toCsvRow(['Phone', driver.driverPhone]));
    }
    if (driver.driverEmail) {
      lines.push(toCsvRow(['Email', driver.driverEmail]));
    }
    lines.push(toCsvRow(['Total Orders', driver.totalOrderCount || 0]));
    lines.push('');

    // Driver Metrics — matches the on-screen metric cards
    const avgPerOrder =
      driver.totalOrderCount > 0
        ? driver.totalAmount / driver.totalOrderCount
        : 0;
    lines.push(
      toCsvRow([
        'Total Amount',
        'Driver Tips',
        'Delivery Fees',
        'Amount Collected',
        'Avg per Order',
      ])
    );
    lines.push(
      toCsvRow([
        formatCurrency(driver.totalAmount || 0),
        formatCurrency(driver.totalDriverTips || 0),
        formatCurrency(driver.totalDeliveryFees || 0),
        formatCurrency(driver.totalAmountCollected || 0),
        formatCurrency(avgPerOrder),
      ])
    );

    // Payment Method Breakdown — matches the on-screen table exactly
    const pb = driver.paymentBreakdown;
    if (pb) {
      lines.push('');
      lines.push(toCsvRow(['Payment Method Breakdown']));
      lines.push(
        toCsvRow([
          'Payment Type',
          'Order Count',
          'Total Amount',
          'Driver Tip',
          'Delivery Fee',
          'Amount Collected',
        ])
      );
      lines.push(
        toCsvRow([
          'Card',
          pb.card?.orderCount || 0,
          formatCurrency(pb.card?.total || 0),
          formatCurrency(pb.card?.driverTip || 0),
          formatCurrency(pb.card?.deliveryFee || 0),
          formatCurrency(pb.card?.amountCollected || 0),
        ])
      );
      lines.push(
        toCsvRow([
          'Cash',
          pb.cash?.orderCount || 0,
          formatCurrency(pb.cash?.total || 0),
          formatCurrency(pb.cash?.driverTip || 0),
          formatCurrency(pb.cash?.deliveryFee || 0),
          formatCurrency(pb.cash?.amountCollected || 0),
        ])
      );
      lines.push(
        toCsvRow([
          'Other',
          pb.other?.orderCount || 0,
          formatCurrency(pb.other?.total || 0),
          formatCurrency(pb.other?.driverTip || 0),
          formatCurrency(pb.other?.deliveryFee || 0),
          formatCurrency(pb.other?.amountCollected || 0),
        ])
      );
      lines.push(
        toCsvRow([
          'Reward',
          pb.reward?.orderCount || 0,
          formatCurrency(pb.reward?.total || 0),
          formatCurrency(pb.reward?.driverTip || 0),
          formatCurrency(pb.reward?.deliveryFee || 0),
          formatCurrency(pb.reward?.amountCollected || 0),
        ])
      );
      lines.push(
        toCsvRow([
          'Unpaid',
          pb.unpaid?.orderCount,
          formatCurrency(pb.unpaid?.total || 0),
          formatCurrency(pb.unpaid?.unpaidDriverTip || 0),
          formatCurrency(pb.unpaid?.unpaidDeliveryFee || 0),
          formatCurrency(pb.unpaid?.amountCollected || 0),
        ])
      );
      lines.push(
        toCsvRow([
          'TOTAL',
          driver.totalOrderCount || 0,
          formatCurrency(driver.totalAmount || 0),
          formatCurrency(driver.totalDriverTips || 0),
          formatCurrency(driver.totalDeliveryFees || 0),
          formatCurrency(driver.totalAmountCollected || 0),
        ])
      );
    }

    return lines;
  };

  // CSV Export function for the full Driver Performance Report
  const exportDriverPerformanceToCSV = () => {
    const driverData = data.driverBreakdown;
    if (!driverData?.breakdown || driverData.breakdown.length === 0) {
      alert('No driver performance data available to export');
      return;
    }

    const csvLines = [];

    csvLines.push(toCsvRow(['Driver Performance Report']));
    csvLines.push(toCsvRow(['Report Period', reportPeriod]));
    csvLines.push('');

    // Summary — matches the on-screen summary cards
    const summary = driverData.summary;
    if (summary) {
      csvLines.push(toCsvRow(['Summary']));
      csvLines.push(
        toCsvRow([
          'Total Drivers',
          'Total Orders',
          'Total Amount',
          'Total Driver Tips',
          'Total Delivery Fees',
          'Amount Collected',
        ])
      );
      csvLines.push(
        toCsvRow([
          summary.totalDrivers || 0,
          summary.totalOrders || 0,
          formatCurrency(summary.totalAmount || 0),
          formatCurrency(summary.totalDriverTips || 0),
          formatCurrency(summary.totalDeliveryFees || 0),
          formatCurrency(summary.totalAmountCollected || 0),
        ])
      );
      csvLines.push('');
    }

    driverData.breakdown.forEach((driver, driverIndex) => {
      if (driverIndex > 0) {
        csvLines.push('');
      }
      csvLines.push(...buildDriverCsvBlock(driver));
    });

    const csvContent = csvLines.join('\n');

    triggerCsvDownload(
      csvContent,
      `driver_performance_${new Date().toISOString().split('T')[0]}.csv`
    );
  };

  // CSV Export function for a single driver
  const exportSingleDriverToCSV = (driver) => {
    if (!driver) {
      alert('No driver data available to export');
      return;
    }

    const csvLines = [];

    csvLines.push(toCsvRow(['Driver Performance Report']));
    csvLines.push(toCsvRow(['Report Period', reportPeriod]));
    csvLines.push('');
    csvLines.push(...buildDriverCsvBlock(driver));

    const csvContent = csvLines.join('\n');

    // Build a filesystem-friendly name from the driver's name.
    const safeName =
      (driver.driverName || 'driver')
        .replace(/[^a-z0-9]+/gi, '_')
        .replace(/^_+|_+$/g, '')
        .toLowerCase() || 'driver';

    triggerCsvDownload(
      csvContent,
      `driver_performance_${safeName}_${
        new Date().toISOString().split('T')[0]
      }.csv`
    );
  };

  // Shared helper to trigger a CSV file download in the browser
  const triggerCsvDownload = (csvContent, fileName) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // CSV Export function for Employee Timesheets
  const exportEmployeeTimesheetsToCSV = () => {
    if (!data.employeeTimesheetReport) {
      alert('No timesheet data available to export');
      return;
    }

    const employees = data.employeeTimesheetReport.employees || [];

    // Helper function to escape CSV cells
    const escapeCell = (cell) => {
      const cellStr = String(cell);
      if (
        cellStr.includes(',') ||
        cellStr.includes('"') ||
        cellStr.includes('\n')
      ) {
        return `"${cellStr.replace(/"/g, '""')}"`;
      }
      return cellStr;
    };

    // Detailed Timesheets Headers
    const detailedHeaders = ['Day', 'Job', 'Time In', 'Time Out', 'Duration'];

    // Create CSV content with grouped employees
    const csvLines = [];

    employees.forEach((emp, empIndex) => {
      // Add employee header with summary
      if (empIndex > 0) {
        csvLines.push(''); // Blank line between employees
      }

      csvLines.push(
        `${emp.employeeFirstName || ''} ${
          emp.employeeLastName || ''
        } - Regular Hours: ${emp.totalHours || '0h 0m'} - Break Hours: ${
          emp.totalBreaks || '0h 0m'
        }`
      );

      // Add headers for this employee's timesheets
      csvLines.push(detailedHeaders.join(','));

      // Add timesheet rows for this employee
      if (emp.timesheets && emp.timesheets.length > 0) {
        emp.timesheets.forEach((ts) => {
          const dayOfWeek = ts.date ? getDayOfWeek(ts.date) : '';
          const timeIn =
            ts.in && ts.date ? `${ts.in} ${formatDate(ts.date)}` : '';
          const timeOut =
            ts.out === 'CLOCK IN'
              ? 'Working'
              : ts.out && ts.shiftEndDate
              ? `${ts.out} ${formatDate(ts.shiftEndDate)}`
              : ts.out || '';

          const row = [
            dayOfWeek,
            ts.jobName || '',
            timeIn,
            timeOut,
            ts.totalHours || '0h 0m',
          ];
          csvLines.push(row.map(escapeCell).join(','));
        });
      }
    });

    const csvContent = csvLines.join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `employee_timesheets_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportEmployeeSalesPerformanceToCSV = () => {
    if (!data.employeeDetailedBreakdown) {
      alert('No employee sales data available to export');
      return;
    }

    const employees = data.employeeDetailedBreakdown.employees || [];

    // Create CSV content
    const csvLines = [];

    employees.forEach((emp, empIndex) => {
      // Add employee header
      if (empIndex > 0) {
        csvLines.push(''); // Blank line between employees
        csvLines.push(''); // Extra blank line for separation
      }

      csvLines.push(
        `${emp.employeeFirstName || ''} ${emp.employeeLastName || ''}`
      );
      csvLines.push(''); // Blank line after employee name

      // Employee Performance Metrics - Daily Breakdown
      csvLines.push('Performance Metrics - Daily Breakdown');
      csvLines.push(''); // Blank line

      if (emp.dailyBreakdown && emp.dailyBreakdown.length > 0) {
        // Calculate totals
        const totals = emp.dailyBreakdown.reduce(
          (acc, day) => ({
            orders: acc.orders + (day.todaysTotalOrders || 0),
            subtotal: acc.subtotal + (day.subtotal || 0),
            tax: acc.tax + (day.tax || 0),
            adjustmentsAndFees:
              acc.adjustmentsAndFees + (day.adjustmentsAndFees || 0),
            amountCollected: acc.amountCollected + (day.amountCollected || 0),
          }),
          {
            orders: 0,
            subtotal: 0,
            tax: 0,
            adjustmentsAndFees: 0,
            amountCollected: 0,
          }
        );

        // Count transactions per day
        const transactionsByDate = {};
        if (emp.detailedReport?.transactions) {
          emp.detailedReport.transactions.forEach((txn) => {
            const txnDate = new Date(txn.createdAt).toISOString().split('T')[0];
            transactionsByDate[txnDate] =
              (transactionsByDate[txnDate] || 0) + 1;
          });
        }

        const totalTransactions = Object.values(transactionsByDate).reduce(
          (sum, count) => sum + count,
          0
        );

        // Headers: Metric, Date1, Date2, ..., Total
        const dateHeaders = [
          'Metric',
          ...emp.dailyBreakdown.map((day) => formatDate(day.date)),
          'Total',
        ];
        csvLines.push(dateHeaders.join(','));

        // Orders row
        const ordersRow = [
          'Orders',
          ...emp.dailyBreakdown.map((day) => day.todaysTotalOrders || 0),
          totals.orders,
        ];
        csvLines.push(ordersRow.join(','));

        // Subtotal row
        const subtotalRow = [
          'Subtotal',
          ...emp.dailyBreakdown.map((day) => formatCurrency(day.subtotal || 0)),
          formatCurrency(totals.subtotal),
        ];
        csvLines.push(subtotalRow.join(','));

        // Tax row
        const taxRow = [
          'Tax',
          ...emp.dailyBreakdown.map((day) => formatCurrency(day.tax || 0)),
          formatCurrency(totals.tax),
        ];
        csvLines.push(taxRow.join(','));

        // Adjustments & Fees row
        const adjustmentsRow = [
          'Adjustments & Fees',
          ...emp.dailyBreakdown.map((day) =>
            formatCurrency(day.adjustmentsAndFees || 0)
          ),
          formatCurrency(totals.adjustmentsAndFees),
        ];
        csvLines.push(adjustmentsRow.join(','));

        // Amount Collected row
        const amountCollectedRow = [
          'Amount Collected',
          ...emp.dailyBreakdown.map((day) =>
            formatCurrency(day.amountCollected || 0)
          ),
          formatCurrency(totals.amountCollected),
        ];
        csvLines.push(amountCollectedRow.join(','));

        // Number of Payments row
        const paymentsRow = [
          'Number of Payments',
          ...emp.dailyBreakdown.map((day) => transactionsByDate[day.date] || 0),
          totalTransactions,
        ];
        csvLines.push(paymentsRow.join(','));
      } else {
        // No daily breakdown available, show totals only
        csvLines.push('Metric,Total');
        csvLines.push('Orders,' + (emp.totalOrders || 0));
        csvLines.push('Subtotal,' + formatCurrency(emp.subtotal || 0));
        csvLines.push('Tax,' + formatCurrency(emp.tax || 0));
        csvLines.push(
          'Adjustments & Fees,' + formatCurrency(emp.adjustmentsAndFees || 0)
        );
        csvLines.push(
          'Amount Collected,' + formatCurrency(emp.amountCollected || 0)
        );
        csvLines.push(
          'Number of Payments,' + (emp.detailedReport?.totalTransactions || 0)
        );
      }
      csvLines.push(''); // Blank line after metrics

      // Tip Breakdown - Detailed Daily
      if (emp.kitchenTipBreakdown) {
        csvLines.push('Tip Breakdown - Detailed Daily Breakdown');
        csvLines.push(''); // Blank line

        // Get active days with tip activity
        const activeTipDays = emp.kitchenTipBreakdown.daily || [];

        if (activeTipDays.length > 0) {
          // Headers: Metric, Date1, Date2, ..., Total
          const dateHeaders = [
            'Metric',
            ...activeTipDays.map((day) => formatDate(day.date)),
            'Total',
          ];
          csvLines.push(dateHeaders.join(','));

          // Non-cash Kitchen Tip Paid row
          const nonCashPaidRow = [
            'Non-cash Kitchen Tip Paid',
            ...activeTipDays.map((day) =>
              formatCurrency(day.purchaseNonCash || 0)
            ),
            formatCurrency(emp.kitchenTipBreakdown.kitchenTipPaidNonCash || 0),
          ];
          csvLines.push(nonCashPaidRow.join(','));

          // Non-cash Kitchen Tip Refunded row
          const nonCashRefundedRow = [
            'Non-cash Kitchen Tip Refunded',
            ...activeTipDays.map((day) =>
              formatCurrency(-Math.abs(day.refundNonCash || 0))
            ),
            formatCurrency(
              -Math.abs(emp.kitchenTipBreakdown.kitchenTipRefundedNonCash || 0)
            ),
          ];
          csvLines.push(nonCashRefundedRow.join(','));

          // Non-cash Kitchen Tip Collected row
          const nonCashCollectedRow = [
            'Non-cash Kitchen Tip Collected',
            ...activeTipDays.map((day) =>
              formatCurrency(
                (day.purchaseNonCash || 0) + (day.refundNonCash || 0)
              )
            ),
            formatCurrency(
              emp.kitchenTipBreakdown.kitchenTipCollectedNonCash || 0
            ),
          ];
          csvLines.push(nonCashCollectedRow.join(','));

          // Cash Kitchen Tip Paid row
          const cashPaidRow = [
            'Cash Kitchen Tip Paid',
            ...activeTipDays.map((day) =>
              formatCurrency(day.purchaseCash || 0)
            ),
            formatCurrency(emp.kitchenTipBreakdown.kitchenTipPaidCash || 0),
          ];
          csvLines.push(cashPaidRow.join(','));

          // Cash Kitchen Tip Refunded row
          const cashRefundedRow = [
            'Cash Kitchen Tip Refunded',
            ...activeTipDays.map((day) =>
              formatCurrency(-Math.abs(day.refundCash || 0))
            ),
            formatCurrency(
              -Math.abs(emp.kitchenTipBreakdown.kitchenTipRefundedCash || 0)
            ),
          ];
          csvLines.push(cashRefundedRow.join(','));

          // Cash Kitchen Tip Collected row
          const cashCollectedRow = [
            'Cash Kitchen Tip Collected',
            ...activeTipDays.map((day) =>
              formatCurrency((day.purchaseCash || 0) + (day.refundCash || 0))
            ),
            formatCurrency(
              emp.kitchenTipBreakdown.kitchenTipCollectedCash || 0
            ),
          ];
          csvLines.push(cashCollectedRow.join(','));
        } else {
          // No daily data, just show totals
          csvLines.push(
            'Non-cash Kitchen Tip Paid,' +
              formatCurrency(emp.kitchenTipBreakdown.kitchenTipPaidNonCash || 0)
          );
          csvLines.push(
            'Non-cash Kitchen Tip Refunded,' +
              formatCurrency(
                -Math.abs(
                  emp.kitchenTipBreakdown.kitchenTipRefundedNonCash || 0
                )
              )
          );
          csvLines.push(
            'Non-cash Kitchen Tip Collected,' +
              formatCurrency(
                emp.kitchenTipBreakdown.kitchenTipCollectedNonCash || 0
              )
          );
          csvLines.push(
            'Cash Kitchen Tip Paid,' +
              formatCurrency(emp.kitchenTipBreakdown.kitchenTipPaidCash || 0)
          );
          csvLines.push(
            'Cash Kitchen Tip Refunded,' +
              formatCurrency(
                -Math.abs(emp.kitchenTipBreakdown.kitchenTipRefundedCash || 0)
              )
          );
          csvLines.push(
            'Cash Kitchen Tip Collected,' +
              formatCurrency(
                emp.kitchenTipBreakdown.kitchenTipCollectedCash || 0
              )
          );
        }
      }
    });

    const csvContent = csvLines.join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `employee_sales_performance_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportTotalProfitReportToCSV = () => {
    if (!data.totalProfitReport) {
      alert('No total profit report data available to export');
      return;
    }

    const escapeCell = (cell) => {
      const cellStr = String(cell ?? '');
      if (
        cellStr.includes(',') ||
        cellStr.includes('"') ||
        cellStr.includes('\n')
      ) {
        return `"${cellStr.replace(/"/g, '""')}"`;
      }
      return cellStr;
    };

    const csvLines = [];
    const r = data.totalProfitReport;

    csvLines.push('Total Profit Report');
    csvLines.push(`Report Period,${reportPeriod}`);
    csvLines.push('');

    csvLines.push('Summary');
    csvLines.push('Metric,Value');
    csvLines.push(
      `Subtotal,${escapeCell(r.totalSubtotal ?? r.totalSalesValue)}`
    );
    csvLines.push(`Cost of Goods Sold,${escapeCell(r.costOfGoodsSold)}`);
    csvLines.push(`Gross Profit,${escapeCell(r.grossProfit)}`);
    csvLines.push(
      `Gross Profit %,${escapeCell(
        r.grossProfitPercent != null
          ? r.grossProfitPercent.toFixed(2) + '%'
          : ''
      )}`
    );
    csvLines.push(`Total Sales Value,${escapeCell(r.totalSalesValue)}`);
    csvLines.push(`Total Tax,${escapeCell(r.totalTax)}`);
    csvLines.push('');

    if (r.daily && r.daily.length > 0) {
      csvLines.push('Daily Breakdown');
      const dailyHeaders = [
        'Date',
        'Subtotal',
        'Cost of Goods Sold',
        'Gross Profit',
        'Gross Profit %',
        'Total Sales Value',
        'Total Tax',
      ];
      csvLines.push(dailyHeaders.map(escapeCell).join(','));
      r.daily.forEach((day) => {
        csvLines.push(
          [
            day.date,
            day.subtotal ?? day.totalSalesValue,
            day.costOfGoodsSold,
            day.grossProfit,
            day.grossProfitPercent != null
              ? day.grossProfitPercent.toFixed(2) + '%'
              : '',
            day.totalSalesValue,
            day.totalTax,
          ]
            .map(escapeCell)
            .join(',')
        );
      });
      csvLines.push('');
    }

    if (r.orders && r.orders.length > 0) {
      csvLines.push('Orders');
      csvLines.push(
        'Date,Order #,Order Number,Sales Value,COGS,Gross Profit,Tax,Item Name,Quantity,Sale Price,Unit Cost,Total Sale Value,Total Cost'
      );
      r.orders.forEach((order) => {
        const orderRow = [
          order.date,
          order.displayNumber,
          order.orderNumber,
          order.totalSalesValue,
          order.costOfGoodsSold,
          order.grossProfit,
          order.tax,
          '',
          '',
          '',
          '',
          '',
          '',
        ];
        csvLines.push(orderRow.map(escapeCell).join(','));
        if (order.items && order.items.length > 0) {
          order.items.forEach((item) => {
            csvLines.push(
              [
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                item.itemName,
                item.quantity,
                item.salePrice,
                item.unitCost,
                item.totalSaleValue,
                item.totalCost,
              ]
                .map(escapeCell)
                .join(',')
            );
          });
        }
      });
    }

    const csvContent = csvLines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `total_profit_report_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Calculate totals across all days
  const calculateTotals = () => {
    if (!data.dailyBreakdown || data.dailyBreakdown.length === 0) {
      return {
        todaysTotalOrders: data.todaysTotalOrders || 0,
        subtotal: data.subtotal || 0,
        tax: data.tax || 0,
        adjustmentsAndFees: data.adjustmentsAndFees || 0,
        totalDeliveryFeeProcessed: data.totalDeliveryFeeProcessed || 0,
        totalDelivery: data.totalDelivery || 0,
        totalTipProcessed: data.totalTipProcessed || 0,
        totalDiscounts: data.totalDiscounts || 0,
        totalRefund: data.totalRefund || 0,
        total: data.total || 0,
        unpaidAmount: data.unpaidAmount || 0,
        amountCollected: data.amountCollected || 0,
      };
    }

    return data.dailyBreakdown.reduce(
      (acc, day) => ({
        todaysTotalOrders: acc.todaysTotalOrders + (day.todaysTotalOrders || 0),
        subtotal: acc.subtotal + (day.subtotal || 0),
        tax: acc.tax + (day.tax || 0),
        adjustmentsAndFees:
          acc.adjustmentsAndFees + (day.adjustmentsAndFees || 0),
        totalDeliveryFeeProcessed:
          acc.totalDeliveryFeeProcessed + (day.totalDeliveryFeeProcessed || 0),
        totalDelivery: acc.totalDelivery + (day.totalDelivery || 0),
        totalTipProcessed: acc.totalTipProcessed + (day.totalTipProcessed || 0),
        totalDiscounts: acc.totalDiscounts + (day.totalDiscounts || 0),
        totalCoupons: acc.totalCoupons + (day.totalCoupons || 0),
        totalPromotions: acc.totalPromotions + (day.totalPromotions || 0),
        totalRefund: acc.totalRefund + (day.totalRefund || 0),
        total: acc.total + (day.total || 0),
        unpaidAmount: acc.unpaidAmount + (day.unpaidAmount || 0),
        amountCollected: acc.amountCollected + (day.amountCollected || 0),
      }),
      {
        todaysTotalOrders: 0,
        subtotal: 0,
        tax: 0,
        adjustmentsAndFees: 0,
        totalDeliveryFeeProcessed: 0,
        totalDelivery: 0,
        totalTipProcessed: 0,
        totalDiscounts: 0,
        totalCoupons: 0,
        totalPromotions: 0,
        totalRefund: 0,
        total: 0,
        unpaidAmount: 0,
        amountCollected: 0,
      }
    );
  };

  const totals = calculateTotals();

  // Filter out days with no values (all zeros) and track active indices
  const activeIndices = [];
  const activeDays = data.dailyBreakdown
    ? data.dailyBreakdown.filter((day, index) => {
        // const isActive =
        //   (day.todaysTotalOrders || 0) > 0 ||
        //   (day.subtotal || 0) !== 0 ||
        //   (day.tax || 0) !== 0 ||
        //   (day.adjustmentsAndFees || 0) !== 0 ||
        //   (day.totalDeliveryFeeProcessed || 0) !== 0 ||
        //   (day.totalTipProcessed || 0) !== 0 ||
        //   (day.totalDiscounts || 0) !== 0 ||
        //   (day.totalRefund || 0) !== 0 ||
        //   (day.total || 0) !== 0 ||
        //   (day.unpaidAmount || 0) !== 0 ||
        //   (day.amountCollected || 0) !== 0;

        //if (isActive) {
        activeIndices.push(index);
        //}
        return true;
      })
    : [];

  // Filter other breakdown data using the same active indices
  const filterByActiveIndices = (dailyArray) => {
    if (!dailyArray || !Array.isArray(dailyArray)) return [];
    return dailyArray.filter((_, index) => activeIndices.includes(index));
  };

  const activePaymentMethods = data.paymentMethods?.daily
    ? filterByActiveIndices(data.paymentMethods.daily)
    : [];

  const activeDeliveryPaymentMethods = data.deliveryPaymentMethods?.daily
    ? filterByActiveIndices(data.deliveryPaymentMethods.daily)
    : [];

  const activeDeliveryFeeDaily = data.deliveryFeeBreakdown?.daily
    ? filterByActiveIndices(data.deliveryFeeBreakdown.daily)
    : [];

  const activeTipBreakdownDaily = data.tipBreakdown?.daily
    ? filterByActiveIndices(data.tipBreakdown.daily)
    : [];

  const activeKitchenTipDaily = data.kitchenTipBreakdown?.daily
    ? filterByActiveIndices(data.kitchenTipBreakdown.daily)
    : [];

  const activeDriverTipDaily = data.driverTipBreakdown?.daily
    ? filterByActiveIndices(data.driverTipBreakdown.daily)
    : [];

  const activeTaxDaily = data.taxBreakdown?.daily
    ? filterByActiveIndices(data.taxBreakdown.daily)
    : [];

  const formatCount = (value) => value;
  const dailyPaymentMethodsBySource = indexDailySources(
    data.paymentMethods?.daily
  );
  const showTender = (tenderType) => isTenderTypeShown(tenderTypes, tenderType);
  const paymentMethodRows = [
    { label: 'Card', field: 'card', tenderType: 'card' },
    { label: 'Cash', field: 'cash', tenderType: 'cash' },
    { label: 'Other', field: 'other', tenderType: 'other' },
    { label: 'Rewards Used', field: 'rewardsUsed', tenderType: 'reward' },
  ].filter(({ tenderType }) => showTender(tenderType));

  const getPaymentMethodsTotal = (paymentMethods) =>
    paymentMethodRows.reduce(
      (sum, { field }) => sum + (paymentMethods?.[field] || 0),
      0
    );

  const renderPaymentMethodsCard = (title, paymentMethods, activeMethods) => (
    <CollapsibleCard className="print-card">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Report Period: {reportPeriod}
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[200px]">Payment Type</TableHead>
                <TableHead className="text-right min-w-[120px] font-bold">
                  Total
                </TableHead>
                {activeMethods.map((day, index) => (
                  <TableHead key={index} className="text-right min-w-[120px]">
                    {formatDate(day.date)}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">Card</TableCell>
                <TableCell className="text-right font-bold">
                  {formatCurrency(paymentMethods.card)}
                </TableCell>
                {activeMethods.map((day, index) => (
                  <TableCell key={index} className="text-right">
                    {formatCurrency(day.card)}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Cash</TableCell>
                <TableCell className="text-right font-bold">
                  {formatCurrency(paymentMethods.cash)}
                </TableCell>
                {activeMethods.map((day, index) => (
                  <TableCell key={index} className="text-right">
                    {formatCurrency(day.cash)}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Other</TableCell>
                <TableCell className="text-right font-bold">
                  {formatCurrency(paymentMethods.other)}
                </TableCell>
                {activeMethods.map((day, index) => (
                  <TableCell key={index} className="text-right">
                    {formatCurrency(day.other)}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Rewards Used</TableCell>
                <TableCell className="text-right font-bold">
                  {formatCurrency(paymentMethods.rewardsUsed)}
                </TableCell>
                {activeMethods.map((day, index) => (
                  <TableCell key={index} className="text-right">
                    {formatCurrency(day.rewardsUsed)}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow className="bg-primary/10">
                <TableCell className="font-bold">TOTAL</TableCell>
                <TableCell className="text-right font-bold text-lg">
                  {formatCurrency(getPaymentMethodsTotal(paymentMethods))}
                </TableCell>
                {activeMethods.map((day, index) => (
                  <TableCell key={index} className="text-right font-bold">
                    {formatCurrency(getPaymentMethodsTotal(day))}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </CollapsibleCard>
  );

  return (
    <CollapsibleCardGroup className="">
      <CollapsibleCardGroupControls className="mb-4" />
      {/* Business Header (Print Only) */}
      {data.businessInfo && (
        <div className="print-only hidden print:block print-card print-avoid-break">
          <div className="text-center border-b border-gray-300 pb-6 mb-6">
            <h1 className="text-3xl font-bold text-black mb-2">
              {data.businessInfo.businessTitle}
            </h1>
            <div className="text-sm text-gray-600 space-y-1">
              <p>{data.businessInfo.businessAddressInfo.fullAddress}</p>
              <p>
                {data.businessInfo.businessAddressInfo.city},{' '}
                {data.businessInfo.businessAddressInfo.state}{' '}
                {data.businessInfo.businessAddressInfo.zipCode}
              </p>
              <p>Phone: {data.businessInfo.businessAdminPhone}</p>
              <p>
                Contact: {data.businessInfo.businessAdminFirstName}{' '}
                {data.businessInfo.businessAdminLastName}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Report Header */}
      {/* <Card className="print-card print-avoid-break">
        <CardContent className="pt-6">
          <div className="border-b border-border pb-4 mb-4">
            {data.businessInfo && (
              <div className="print:hidden mb-4 p-4 bg-muted rounded-lg">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">
                      {data.businessInfo.businessTitle}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {data.businessInfo.businessAddressInfo.city},{' '}
                      {data.businessInfo.businessAddressInfo.state}
                    </p>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    <p>
                      {data.businessInfo.businessAdminFirstName}{' '}
                      {data.businessInfo.businessAdminLastName}
                    </p>
                    <p>{data.businessInfo.businessAdminPhone}</p>
                  </div>
                </div>
              </div>
            )}

            <h2 className="text-2xl font-bold text-foreground print-title">
              POS Performance Report
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm text-muted-foreground">
              <div>
                <span className="font-medium">Report Period:</span>
                <span className="ml-2">
                  {formatDate(data.dateRange.startDate)} -{' '}
                  {formatDate(data.dateRange.endDate)}
                </span>
              </div>
              <div>
                <span className="font-medium">Generated:</span>
                <span className="ml-2">{formatDateTime()}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card> */}

      {/* Sales Summary Cards */}
      <CollapsibleCard className="print-card" defaultOpen>
        <CardHeader>
          <CardTitle className="flex items-center">
            <BarChart3 className="text-primary mr-3 h-5 w-5" />
            Sales Summary
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Report Period: {reportPeriod}
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Total Orders
                </p>
                <p className="text-3xl font-bold text-foreground">
                  {totals.todaysTotalOrders}
                </p>
                <SourceBreakdownList
                  sources={data.totalOrdersBySource?.sources}
                  format={formatCount}
                />
              </div>
              <div className="bg-primary/10 p-3 rounded-full">
                <ShoppingCart className="text-primary h-6 w-6" />
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Subtotal
                </p>
                <p className="text-3xl font-bold text-foreground">
                  {formatCurrency(totals.subtotal)}
                </p>
                <SourceBreakdownList
                  sources={data.subtotalBySource?.sources}
                  format={formatCurrency}
                />
              </div>
              <div className="bg-primary/10 p-3 rounded-full">
                <DollarSign className="text-primary h-6 w-6" />
              </div>
            </div>

            {data.amountCollected != null && (
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Amount Collected
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(totals.amountCollected)}
                  </p>
                  <SourceBreakdownList
                    sources={data.amountCollectedBySource?.sources}
                    format={formatCurrency}
                  />
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <DollarSign className="text-green-600 h-5 w-5" />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </CollapsibleCard>

      {/* Customer Analytics */}
      {data.customerReport && (
        <CollapsibleCard className="print-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="text-primary mr-3 h-5 w-5" />
              Customer Analytics
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Report Period: {reportPeriod}
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div className="bg-muted p-4 rounded-lg">
                <h4 className="font-medium text-foreground mb-2">
                  Total Customers
                </h4>
                <div className="text-2xl font-bold text-foreground">
                  {(
                    data.customerReport.summary?.totalCustomers || 0
                  ).toLocaleString()}
                </div>
              </div>

              <div className="bg-muted p-4 rounded-lg">
                <h4 className="font-medium text-foreground mb-2">
                  Guest Customers
                </h4>
                <div className="text-2xl font-bold text-foreground">
                  {(
                    data.customerReport.summary?.totalGuestCustomers || 0
                  ).toLocaleString()}
                </div>
              </div>

              <div className="bg-muted p-4 rounded-lg">
                <h4 className="font-medium text-foreground mb-2">
                  New Customers
                </h4>
                <div className="text-2xl font-bold text-foreground">
                  {(
                    data.customerReport.summary?.totalNewCustomers || 0
                  ).toLocaleString()}
                </div>
              </div>

              <div className="bg-muted p-4 rounded-lg">
                <h4 className="font-medium text-foreground mb-2">
                  Returning Customers
                </h4>
                <div className="text-2xl font-bold text-foreground">
                  {(
                    data.customerReport.summary?.totalReturningCustomers || 0
                  ).toLocaleString()}
                </div>
              </div>
            </div>

            {/* Customer Breakdown by Segment */}
            {/* {data.customerReport.breakdownBySegment &&
              data.customerReport.breakdownBySegment.length > 0 && (
                <div className="overflow-x-auto mt-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Segment</TableHead>
                        <TableHead className="text-right">Customers</TableHead>
                        <TableHead className="text-right">Orders</TableHead>
                        <TableHead className="text-right">
                          Total Spent
                        </TableHead>
                        <TableHead className="text-right">Avg Spent</TableHead>
                        <TableHead className="text-right">% of Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.customerReport.breakdownBySegment.map(
                        (segment, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium capitalize">
                              {segment.segment}
                            </TableCell>
                            <TableCell className="text-right">
                              {segment.customerCount.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right">
                              {segment.orderCount.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(segment.totalSpent)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(segment.averageSpent)}
                            </TableCell>
                            <TableCell className="text-right">
                              {segment.percentageOfTotalCustomers.toFixed(1)}%
                            </TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                </div>
              )} */}
          </CardContent>
        </CollapsibleCard>
      )}

      {/* Daily Breakdown Table */}
      {showDailyBreakdown && !hasDailyBreakdown && (
        <CollapsibleCard className="print-card">
          <CardHeader>
            <CardTitle>Daily Performance Breakdown</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Report Period: {reportPeriod}
            </p>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              No data available for this section.
            </p>
          </CardContent>
        </CollapsibleCard>
      )}
      {showDailyBreakdown && hasDailyBreakdown && (
        <CollapsibleCard className="print-card">
          <CardHeader>
            <CardTitle>Daily Performance Breakdown</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Report Period: {reportPeriod}
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[200px]">Metric</TableHead>
                    <TableHead className="text-right min-w-[120px] font-bold">
                      {'Total'}
                    </TableHead>
                    {activeDays.map((day, index) => (
                      <TableHead
                        key={index}
                        className="text-right min-w-[120px]"
                      >
                        {formatDate(day.date)}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Orders</TableCell>
                    <TableCell className="text-right font-bold">
                      {totals.todaysTotalOrders}
                    </TableCell>
                    {activeDays.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {day.todaysTotalOrders}
                      </TableCell>
                    ))}
                  </TableRow>

                  <TableRow>
                    <TableCell className="font-medium">Subtotal</TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(totals.subtotal)}
                    </TableCell>
                    {activeDays.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {formatCurrency(day.subtotal)}
                      </TableCell>
                    ))}
                  </TableRow>

                  <TableRow>
                    <TableCell className="font-medium">Tax</TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(totals.tax)}
                    </TableCell>
                    {activeDays.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {formatCurrency(day.tax)}
                      </TableCell>
                    ))}
                  </TableRow>

                  <TableRow>
                    <TableCell className="font-medium">
                      Adjustments & Fees
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(totals.adjustmentsAndFees)}
                    </TableCell>
                    {activeDays.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {formatCurrency(day.adjustmentsAndFees)}
                      </TableCell>
                    ))}
                  </TableRow>

                  {data.deliveryFeeBreakdown != null && (
                    <TableRow>
                      <TableCell className="font-medium">Delivery</TableCell>
                      <TableCell className="text-right font-bold">
                        {formatCurrency(totals.totalDelivery)}
                      </TableCell>
                      {activeDays.map((day, index) => (
                        <TableCell key={index} className="text-right">
                          {formatCurrency(day.totalDelivery || 0)}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}

                  {data.tipBreakdown != null && (
                    <TableRow>
                      <TableCell className="font-medium">Tips</TableCell>
                      <TableCell className="text-right font-bold">
                        {formatCurrency(totals.totalTipProcessed)}
                      </TableCell>
                      {activeDays.map((day, index) => (
                        <TableCell key={index} className="text-right">
                          {formatCurrency(day.totalTipProcessed)}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}

                  <TableRow>
                    <TableCell className="font-medium">Discounts</TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(-Math.abs(totals.totalDiscounts))}
                    </TableCell>
                    {activeDays.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {formatCurrency(-Math.abs(day.totalDiscounts))}
                      </TableCell>
                    ))}
                  </TableRow>

                  <TableRow>
                    <TableCell className="font-medium">Coupons</TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(-Math.abs(totals.totalCoupons))}
                    </TableCell>
                    {activeDays.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {formatCurrency(-Math.abs(day.totalCoupons))}
                      </TableCell>
                    ))}
                  </TableRow>

                  <TableRow>
                    <TableCell className="font-medium">Promotions</TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(-Math.abs(totals.totalPromotions))}
                    </TableCell>
                    {activeDays.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {formatCurrency(-Math.abs(day.totalPromotions))}
                      </TableCell>
                    ))}
                  </TableRow>

                  <TableRow className="bg-primary/10">
                    <TableCell className="font-bold">TOTAL</TableCell>
                    <TableCell className="text-right font-bold text-lg">
                      {formatCurrency(totals.total)}
                    </TableCell>
                    {activeDays.map((day, index) => (
                      <TableCell key={index} className="text-right font-bold">
                        {formatCurrency(day.total)}
                      </TableCell>
                    ))}
                  </TableRow>

                  {data.refundsBreakdown != null && (
                    <TableRow>
                      <TableCell className="font-medium">Refunds</TableCell>
                      <TableCell className="text-right font-bold">
                        {formatCurrency(-Math.abs(totals.totalRefund))}
                      </TableCell>
                      {activeDays.map((day, index) => (
                        <TableCell key={index} className="text-right">
                          {formatCurrency(-Math.abs(day.totalRefund))}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}

                  <TableRow>
                    <TableCell className="font-medium">Unpaid Amount</TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(totals.unpaidAmount)}
                    </TableCell>
                    {activeDays.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {formatCurrency(day.unpaidAmount)}
                      </TableCell>
                    ))}
                  </TableRow>

                  {data.amountCollected != null && (
                    <TableRow className="border-t-2">
                      <TableCell className="font-bold">
                        Amount Collected
                      </TableCell>
                      <TableCell className="text-right font-bold text-lg">
                        {formatCurrency(totals.amountCollected)}
                      </TableCell>
                      {activeDays.map((day, index) => (
                        <TableCell key={index} className="text-right font-bold">
                          {formatCurrency(day.amountCollected)}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </CollapsibleCard>
      )}

      {/* Payment Methods */}
      {data.paymentMethods && (
        <CollapsibleCard className="print-card">
          <CardHeader>
            <CardTitle>
              {isDeliveryReport
                ? 'Delivery Payment Methods'
                : 'Payment Methods'}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Report Period: {reportPeriod}
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[200px] whitespace-nowrap">
                      Payment Type
                    </TableHead>
                    <TableHead className="text-right min-w-[120px] font-bold">
                      Total
                    </TableHead>
                    {activePaymentMethods.map((day, index) => (
                      <TableHead
                        key={index}
                        className="text-right min-w-[120px]"
                      >
                        {formatDate(day.date)}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paymentMethodRows.map(({ label, field }) => (
                    <SourceRow
                      key={field}
                      label={label}
                      total={data.paymentMethods[field]}
                      days={activePaymentMethods.map((day) => ({
                        date: day.date,
                        value: day[field],
                      }))}
                      sources={data.paymentMethods[`${field}BySource`]}
                      getDaySources={(date) =>
                        dailyPaymentMethodsBySource(date, `${field}BySource`)
                      }
                      format={formatCurrency}
                    />
                  ))}
                  <SourceRow
                    label="TOTAL"
                    total={getPaymentMethodsTotal(data.paymentMethods)}
                    days={activePaymentMethods.map((day) => ({
                      date: day.date,
                      value: getPaymentMethodsTotal(day),
                    }))}
                    sources={sumSources(
                      paymentMethodRows.map(
                        ({ field }) => data.paymentMethods[`${field}BySource`]
                      )
                    )}
                    getDaySources={(date) =>
                      sumSources(
                        paymentMethodRows.map(({ field }) =>
                          dailyPaymentMethodsBySource(date, `${field}BySource`)
                        )
                      )
                    }
                    format={formatCurrency}
                    rowClassName="bg-primary/10"
                    labelClassName="font-bold"
                    totalClassName="text-right font-bold text-lg"
                    dayClassName="text-right font-bold"
                  />
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </CollapsibleCard>
      )}

      {/* Delivery Fee Breakdown */}
      {data.deliveryFeeBreakdown && (
        <CollapsibleCard className="print-card">
          <CardHeader>
            <CardTitle>Delivery Fee Breakdown</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Report Period: {reportPeriod}
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[200px]">
                      Delivery Type
                    </TableHead>
                    <TableHead className="text-right min-w-[120px] font-bold">
                      Total
                    </TableHead>
                    {activeDeliveryFeeDaily.map((day, index) => (
                      <TableHead
                        key={index}
                        className="text-right min-w-[120px]"
                      >
                        {formatDate(day.date)}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Self Delivery</TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(
                        data.deliveryFeeBreakdown.selfDelivery ?? 0
                      )}
                    </TableCell>
                    {activeDeliveryFeeDaily.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {formatCurrency(day.selfDelivery ?? 0)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">
                      Orders.co Dispatch
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(
                        data.deliveryFeeBreakdown.ordersCoDispatch ?? 0
                      )}
                    </TableCell>
                    {activeDeliveryFeeDaily.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {formatCurrency(day.ordersCoDispatch ?? 0)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="bg-primary/10">
                    <TableCell className="font-bold">
                      Total Delivery Fee
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(
                        (data.deliveryFeeBreakdown.selfDelivery ?? 0) +
                          (data.deliveryFeeBreakdown.ordersCoDispatch ?? 0)
                      )}
                    </TableCell>
                    {activeDeliveryFeeDaily.map((day, index) => (
                      <TableCell key={index} className="text-right font-bold">
                        {formatCurrency(
                          (day.selfDelivery ?? 0) + (day.ordersCoDispatch ?? 0)
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </CollapsibleCard>
      )}

      {/* Tip Breakdown */}
      {data.tipBreakdown && (
        <CollapsibleCard className="print-card">
          <CardHeader>
            <CardTitle>Tip Breakdown</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Report Period: {reportPeriod}
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[200px]">Tip Type</TableHead>
                    <TableHead className="text-right min-w-[120px] font-bold">
                      Total
                    </TableHead>
                    {activeTipBreakdownDaily.map((day, index) => (
                      <TableHead
                        key={index}
                        className="text-right min-w-[120px]"
                      >
                        {formatDate(day.date)}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Kitchen Tip</TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(data.tipBreakdown.kitchenTip ?? 0)}
                    </TableCell>
                    {activeTipBreakdownDaily.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {formatCurrency(day.kitchenTip ?? 0)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Driver Tip</TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(data.tipBreakdown.driverTip ?? 0)}
                    </TableCell>
                    {activeTipBreakdownDaily.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {formatCurrency(day.driverTip ?? 0)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="bg-muted/20">
                    <TableCell className="pl-8 text-sm text-muted-foreground">
                      • Self Delivery
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {formatCurrency(data.tipBreakdown.selfDelivery ?? 0)}
                    </TableCell>
                    {activeTipBreakdownDaily.map((day, index) => (
                      <TableCell key={index} className="text-right text-sm">
                        {formatCurrency(day.selfDelivery ?? 0)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="bg-muted/20">
                    <TableCell className="pl-8 text-sm text-muted-foreground">
                      • Orders.co Dispatch
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {formatCurrency(data.tipBreakdown.ordersCoDispatch ?? 0)}
                    </TableCell>
                    {activeTipBreakdownDaily.map((day, index) => (
                      <TableCell key={index} className="text-right text-sm">
                        {formatCurrency(day.ordersCoDispatch ?? 0)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="bg-primary/10">
                    <TableCell className="font-bold">Total Tip</TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(
                        (data.tipBreakdown.kitchenTip ?? 0) +
                          (data.tipBreakdown.driverTip ?? 0)
                      )}
                    </TableCell>
                    {activeTipBreakdownDaily.map((day, index) => (
                      <TableCell key={index} className="text-right font-bold">
                        {formatCurrency(
                          (day.kitchenTip ?? 0) + (day.driverTip ?? 0)
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </CollapsibleCard>
      )}

      {/* Kitchen Tip Breakdown */}
      {data.kitchenTipBreakdown && (
        <CollapsibleCard className="print-card">
          <CardHeader>
            <CardTitle>Kitchen Tip Breakdown</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Report Period: {reportPeriod}
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[200px]">Metric</TableHead>
                    <TableHead className="text-right min-w-[120px] font-bold">
                      Total
                    </TableHead>
                    {activeKitchenTipDaily.map((day, index) => (
                      <TableHead
                        key={index}
                        className="text-right min-w-[120px]"
                      >
                        {formatDate(day.date)}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">
                      Non-cash Kitchen Tip Paid
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(
                        data.kitchenTipBreakdown.kitchenTipPaidNonCash || 0
                      )}
                    </TableCell>
                    {activeKitchenTipDaily.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {formatCurrency(day.purchaseNonCash || 0)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">
                      Non-cash Kitchen Tip Refunded
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(
                        -Math.abs(
                          data.kitchenTipBreakdown.kitchenTipRefundedNonCash ||
                            0
                        )
                      )}
                    </TableCell>
                    {activeKitchenTipDaily.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {formatCurrency(-Math.abs(day.refundNonCash || 0))}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="bg-primary/10">
                    <TableCell className="font-bold">
                      Non-cash Kitchen Tip Collected
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(
                        data.kitchenTipBreakdown.kitchenTipCollectedNonCash || 0
                      )}
                    </TableCell>
                    {activeKitchenTipDaily.map((day, index) => (
                      <TableCell key={index} className="text-right font-bold">
                        {formatCurrency(
                          (day.purchaseNonCash || 0) + (day.refundNonCash || 0)
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">
                      Cash Kitchen Tip Paid
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(
                        data.kitchenTipBreakdown.kitchenTipPaidCash || 0
                      )}
                    </TableCell>
                    {activeKitchenTipDaily.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {formatCurrency(day.purchaseCash || 0)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">
                      Cash Kitchen Tip Refunded
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(
                        -Math.abs(
                          data.kitchenTipBreakdown.kitchenTipRefundedCash || 0
                        )
                      )}
                    </TableCell>
                    {activeKitchenTipDaily.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {formatCurrency(-Math.abs(day.refundCash || 0))}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="bg-primary/10">
                    <TableCell className="font-bold">
                      Cash Kitchen Tip Collected
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(
                        data.kitchenTipBreakdown.kitchenTipCollectedCash || 0
                      )}
                    </TableCell>
                    {activeKitchenTipDaily.map((day, index) => (
                      <TableCell key={index} className="text-right font-bold">
                        {formatCurrency(
                          (day.purchaseCash || 0) + (day.refundCash || 0)
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </CollapsibleCard>
      )}

      {/* Driver Tip Breakdown */}
      {data.driverTipBreakdown && (
        <CollapsibleCard className="print-card">
          <CardHeader>
            <CardTitle>Driver Tip Breakdown</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Report Period: {reportPeriod}
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[200px]">Metric</TableHead>
                    <TableHead className="text-right min-w-[120px] font-bold">
                      Total
                    </TableHead>
                    {activeDriverTipDaily.map((day, index) => (
                      <TableHead
                        key={index}
                        className="text-right min-w-[120px]"
                      >
                        {formatDate(day.date)}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">
                      Driver Tip Paid
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(data.driverTipBreakdown.driverTipPaid)}
                    </TableCell>
                    {activeDriverTipDaily.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {formatCurrency(day.purchase)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">
                      Driver Tip Refunded
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(
                        -Math.abs(data.driverTipBreakdown.driverTipRefunded)
                      )}
                    </TableCell>
                    {activeDriverTipDaily.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {formatCurrency(-Math.abs(day.refund))}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="bg-primary/10">
                    <TableCell className="font-bold">
                      Driver Tip Collected
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(
                        data.driverTipBreakdown.driverTipCollected
                      )}
                    </TableCell>
                    {activeDriverTipDaily.map((day, index) => (
                      <TableCell key={index} className="text-right font-bold">
                        {formatCurrency(day.purchase + day.refund)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </CollapsibleCard>
      )}

      {/* Tax Breakdown */}
      {data.taxBreakdown && (
        <CollapsibleCard className="print-card">
          <CardHeader>
            <CardTitle>Tax Breakdown</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Report Period: {reportPeriod}
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[200px]">Metric</TableHead>
                    <TableHead className="text-right min-w-[120px] font-bold">
                      Total
                    </TableHead>
                    {activeTaxDaily.map((day, index) => (
                      <TableHead
                        key={index}
                        className="text-right min-w-[120px]"
                      >
                        {formatDate(day.date)}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Tax Processed</TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(data.taxBreakdown.taxProcessed)}
                    </TableCell>
                    {activeTaxDaily.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {formatCurrency(day.purchase)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Tax Refunded</TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(
                        -Math.abs(data.taxBreakdown.priorTaxRefunded)
                      )}
                    </TableCell>
                    {activeTaxDaily.map((day, index) => (
                      <TableCell key={index} className="text-right">
                        {formatCurrency(-Math.abs(day.refund))}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="bg-primary/10">
                    <TableCell className="font-bold">Tax Collected</TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(data.taxBreakdown.taxCollected)}
                    </TableCell>
                    {activeTaxDaily.map((day, index) => (
                      <TableCell key={index} className="text-right font-bold">
                        {formatCurrency(day.purchase + day.refund)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </CollapsibleCard>
      )}

      {/* Refunds Breakdown */}
      {data.refundsBreakdown && (
        <CollapsibleCard className="print-card">
          <CardHeader>
            <CardTitle>Refunds Breakdown</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Report Period: {reportPeriod}
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Order Refunds</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(
                      -Math.abs(data.refundsBreakdown.todaysOrderRefunds)
                    )}
                  </p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Prior Order Refunds
                  </p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(
                      -Math.abs(data.refundsBreakdown.priorOrderRefunds)
                    )}
                  </p>
                </div>
                <div className="p-4 bg-primary/10 rounded-lg">
                  <p className="text-sm font-medium">Total Refund Processed</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(
                      -Math.abs(data.refundsBreakdown.totalRefundProcessed)
                    )}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </CollapsibleCard>
      )}

      {/* Orders.co Dispatch Breakdown */}
      {data.ordersCoDispatchBreakdown && (
        <CollapsibleCard className="print-card">
          <CardHeader>
            <CardTitle>Orders.co Dispatch Delivery Breakdown</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Report Period: {reportPeriod}
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Delivery Fee Cost
                  </p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(
                      data.ordersCoDispatchBreakdown.deliveryCost
                    )}
                  </p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Delivery Tip</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(data.ordersCoDispatchBreakdown.deliveryTip)}
                  </p>
                </div>
                <div className="p-4 bg-primary/10 rounded-lg">
                  <p className="text-sm font-medium">Amount Due to Orders.co</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(
                      data.ordersCoDispatchBreakdown.amountDueToOrdersCo
                    )}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </CollapsibleCard>
      )}

      {/* Detailed Orders */}
      {data.detailedReport?.orders && data.detailedReport.orders.length > 0 && (
        <VirtualizedOrderTable
          serverPagination={data.detailedReport.pagination?.orders}
          onPageChange={onOrdersPageChange}
          isPageLoading={isOrdersPageLoading}
          onExportAll={
            onExportAllOrders
              ? async () => {
                  const orders = await onExportAllOrders();
                  return orders ? orders.map(toDetailedOrderRow) : null;
                }
              : undefined
          }
          data={{
            orders: data.detailedReport.orders.map(toDetailedOrderRow),
            dateRange: data.dateRange,
          }}
        />
      )}

      {/* Employee Timesheet Report */}
      {data.employeeTimesheetReport && (
        <CollapsibleCard className="print-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center">
                  <Clock className="text-primary mr-3 h-5 w-5" />
                  Employee/Timesheet Report
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Report Period: {reportPeriod}
                </p>
              </div>
              <button
                onClick={exportEmployeeTimesheetsToCSV}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors print:hidden"
              >
                <Download className="h-4 w-4" />
                Export to CSV
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Summary Section */}
            {/* <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              <div className="bg-muted p-4 rounded-lg">
                <h4 className="text-sm font-medium text-muted-foreground mb-1">
                  Total Shifts
                </h4>
                <div className="text-2xl font-bold text-foreground">
                  {data.employeeTimesheetReport.summary?.totalShifts || 0}
                </div>
              </div>

              <div className="bg-muted p-4 rounded-lg">
                <h4 className="text-sm font-medium text-muted-foreground mb-1">
                  Total Employees
                </h4>
                <div className="text-2xl font-bold text-foreground">
                  {data.employeeTimesheetReport.summary?.totalEmployees || 0}
                </div>
              </div>

              <div className="bg-muted p-4 rounded-lg">
                <h4 className="text-sm font-medium text-muted-foreground mb-1">
                  Total Hours
                </h4>
                <div className="text-2xl font-bold text-foreground">
                  {data.employeeTimesheetReport.summary?.totalHours || '0h 0m'}
                </div>
              </div>

              <div className="bg-muted p-4 rounded-lg">
                <h4 className="text-sm font-medium text-muted-foreground mb-1">
                  Total Breaks
                </h4>
                <div className="text-2xl font-bold text-foreground">
                  {data.employeeTimesheetReport.summary?.totalBreaks || '0h 0m'}
                </div>
              </div>

              <div className="bg-muted p-4 rounded-lg">
                <h4 className="text-sm font-medium text-muted-foreground mb-1">
                  Avg Hours/Shift
                </h4>
                <div className="text-2xl font-bold text-foreground">
                  {data.employeeTimesheetReport.summary?.averageHoursPerShift?.toFixed(
                    1
                  ) || 0}
                </div>
              </div>

              <div className="bg-muted p-4 rounded-lg">
                <h4 className="text-sm font-medium text-muted-foreground mb-1">
                  Avg Hours/Employee
                </h4>
                <div className="text-2xl font-bold text-foreground">
                  {data.employeeTimesheetReport.summary?.averageHoursPerEmployee?.toFixed(
                    1
                  ) || 0}
                </div>
              </div>
            </div> */}

            {/* Employee Timesheets Grouped by Employee */}
            {data.employeeTimesheetReport.employees &&
              data.employeeTimesheetReport.employees.length > 0 && (
                <div className="space-y-6">
                  {data.employeeTimesheetReport.employees.map(
                    (emp, empIndex) => (
                      <div key={empIndex} className="print-avoid-break">
                        {/* Employee Header */}
                        <div className="bg-primary/5 p-3 rounded-t-lg border-b-2 border-primary">
                          <div className="flex items-center justify-between flex-wrap gap-4">
                            <h3 className="text-base font-bold text-foreground">
                              {emp.employeeFirstName || ''}{' '}
                              {emp.employeeLastName || ''}
                            </h3>
                            <div className="flex gap-8 text-sm font-medium">
                              <span className="text-foreground">
                                Regular Hours:{emp.totalHours || '0h 0m'}
                              </span>
                              <span className="text-foreground">
                                Break Hours:{emp.totalBreaks || '0h 0m'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Employee's Timesheets */}
                        {emp.timesheets && emp.timesheets.length > 0 && (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="min-w-[80px]">
                                    Day
                                  </TableHead>
                                  <TableHead className="min-w-[120px]">
                                    Job
                                  </TableHead>
                                  <TableHead className="min-w-[150px]">
                                    Time In
                                  </TableHead>
                                  <TableHead className="min-w-[150px]">
                                    Time Out
                                  </TableHead>
                                  <TableHead className="text-right min-w-[120px]">
                                    Duration
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {emp.timesheets.map((ts, tsIndex) => (
                                  <TableRow key={tsIndex}>
                                    <TableCell className="font-medium">
                                      {getDayOfWeek(ts.date)}
                                    </TableCell>
                                    <TableCell className="font-medium">
                                      {ts.jobName || '-'}
                                    </TableCell>
                                    <TableCell className="whitespace-pre-line">
                                      {ts.in && ts.date ? (
                                        <>
                                          {ts.in}
                                          <br />
                                          <span className="text-xs text-muted-foreground">
                                            {formatDate(ts.date)}
                                          </span>
                                        </>
                                      ) : (
                                        '-'
                                      )}
                                    </TableCell>
                                    <TableCell className="whitespace-pre-line">
                                      {ts.out === 'CLOCK IN' ? (
                                        <span className="text-primary font-semibold">
                                          Working
                                        </span>
                                      ) : ts.out && ts.shiftEndDate ? (
                                        <>
                                          {ts.out}
                                          <br />
                                          <span className="text-xs text-muted-foreground">
                                            {formatDate(ts.shiftEndDate)}
                                          </span>
                                        </>
                                      ) : (
                                        ts.out || '-'
                                      )}
                                    </TableCell>
                                    <TableCell className="text-right font-medium">
                                      {ts.totalHours || '0h 0m'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>
              )}
          </CardContent>
        </CollapsibleCard>
      )}

      {/* Employee Sales Performance Report */}
      {data.employeeDetailedBreakdown?.employees &&
        data.employeeDetailedBreakdown.employees.length > 0 && (
          <CollapsibleCard className="print-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center">
                    <Users className="text-primary mr-3 h-5 w-5" />
                    Employee Sales Performance
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Report Period: {reportPeriod}
                  </p>
                </div>
                <button
                  onClick={exportEmployeeSalesPerformanceToCSV}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors print:hidden"
                >
                  <Download className="h-4 w-4" />
                  Export to CSV
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="">
                {data.employeeDetailedBreakdown.employees.map(
                  (emp, empIndex) => {
                    return (
                      <div key={empIndex} className="print-avoid-break">
                        {empIndex > 0 && (
                          <div className="border-t-2 border-gray-300 my-8"></div>
                        )}

                        {/* Employee Header */}
                        <div className="mb-4 flex items-center justify-between">
                          <h4 className="text-lg font-bold text-foreground">
                            {emp.employeeFirstName} {emp.employeeLastName}
                          </h4>
                          {emp.kitchenTipBreakdown?.daily && (
                            <button
                              onClick={() => setSelectedEmployee(emp)}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary hover:text-primary/80 hover:bg-primary/10 rounded-lg transition-colors"
                            >
                              More Details
                              <ChevronRight className="h-4 w-4" />
                            </button>
                          )}
                        </div>

                        {/* Employee Performance Metrics */}
                        <div className="overflow-x-auto mb-6">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-4 bg-muted rounded-lg">
                              <p className="text-sm text-muted-foreground mb-1">
                                Orders
                              </p>
                              <p className="text-2xl font-bold text-foreground">
                                {emp.totalOrders || 0}
                              </p>
                            </div>
                            <div className="p-4 bg-muted rounded-lg">
                              <p className="text-sm text-muted-foreground mb-1">
                                Subtotal
                              </p>
                              <p className="text-2xl font-bold text-foreground">
                                {formatCurrency(emp.subtotal || 0)}
                              </p>
                            </div>
                            <div className="p-4 bg-muted rounded-lg">
                              <p className="text-sm text-muted-foreground mb-1">
                                Tax
                              </p>
                              <p className="text-2xl font-bold text-foreground">
                                {formatCurrency(emp.tax || 0)}
                              </p>
                            </div>
                            <div className="p-4 bg-muted rounded-lg">
                              <p className="text-sm text-muted-foreground mb-1">
                                Adjustments & Fees
                              </p>
                              <p className="text-2xl font-bold text-foreground">
                                {formatCurrency(emp.adjustmentsAndFees || 0)}
                              </p>
                            </div>
                            {data.amountCollected != null && (
                              <div className="p-4 bg-muted rounded-lg">
                                <p className="text-sm text-muted-foreground mb-1">
                                  Amount Collected
                                </p>
                                <p className="text-2xl font-bold text-foreground">
                                  {formatCurrency(emp.amountCollected || 0)}
                                </p>
                              </div>
                            )}
                            <div className="p-4 bg-muted rounded-lg">
                              <p className="text-sm text-muted-foreground mb-1">
                                Number of Payments
                              </p>
                              <p className="text-2xl font-bold text-foreground">
                                {emp.detailedReport?.totalTransactions || 0}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Employee Tip Details */}
                        {emp.kitchenTipBreakdown && (
                          <div className="overflow-x-auto mb-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="p-4 bg-muted rounded-lg">
                                <p className="text-sm text-muted-foreground mb-1">
                                  Non-cash Kitchen Tip Paid
                                </p>
                                <p className="text-2xl font-bold text-foreground">
                                  {formatCurrency(
                                    emp.kitchenTipBreakdown
                                      .kitchenTipPaidNonCash || 0
                                  )}
                                </p>
                              </div>
                              <div className="p-4 bg-muted rounded-lg">
                                <p className="text-sm text-muted-foreground mb-1">
                                  Non-cash Kitchen Tip Refunded
                                </p>
                                <p className="text-2xl font-bold text-foreground">
                                  {formatCurrency(
                                    -Math.abs(
                                      emp.kitchenTipBreakdown
                                        .kitchenTipRefundedNonCash || 0
                                    )
                                  )}
                                </p>
                              </div>
                              <div className="p-4 bg-primary/10 rounded-lg">
                                <p className="text-sm font-medium mb-1">
                                  Non-cash Kitchen Tip Collected
                                </p>
                                <p className="text-2xl font-bold text-foreground">
                                  {formatCurrency(
                                    emp.kitchenTipBreakdown
                                      .kitchenTipCollectedNonCash || 0
                                  )}
                                </p>
                              </div>
                              <div className="p-4 bg-muted rounded-lg">
                                <p className="text-sm text-muted-foreground mb-1">
                                  Cash Kitchen Tip Paid
                                </p>
                                <p className="text-2xl font-bold text-foreground">
                                  {formatCurrency(
                                    emp.kitchenTipBreakdown
                                      .kitchenTipPaidCash || 0
                                  )}
                                </p>
                              </div>
                              <div className="p-4 bg-muted rounded-lg">
                                <p className="text-sm text-muted-foreground mb-1">
                                  Cash Kitchen Tip Refunded
                                </p>
                                <p className="text-2xl font-bold text-foreground">
                                  {formatCurrency(
                                    -Math.abs(
                                      emp.kitchenTipBreakdown
                                        .kitchenTipRefundedCash || 0
                                    )
                                  )}
                                </p>
                              </div>
                              <div className="p-4 bg-primary/10 rounded-lg">
                                <p className="text-sm font-medium mb-1">
                                  Cash Kitchen Tip Collected
                                </p>
                                <p className="text-2xl font-bold text-foreground">
                                  {formatCurrency(
                                    emp.kitchenTipBreakdown
                                      .kitchenTipCollectedCash || 0
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            </CardContent>
          </CollapsibleCard>
        )}

      {/* Driver Breakdown Report */}
      {showDriverBreakdown && (
        <CollapsibleCard className="print-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center">
                  <Users className="text-primary mr-3 h-5 w-5" />
                  Driver Performance Report
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Report Period: {reportPeriod}
                </p>
              </div>
              {hasDriverBreakdown && (
                <button
                  onClick={exportDriverPerformanceToCSV}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors print:hidden"
                >
                  <Download className="h-4 w-4" />
                  Export to CSV
                </button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Summary Section */}
            <div className="mb-8">
              <h3 className="text-base font-semibold mb-4">Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">
                    Total Drivers
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {data.driverBreakdown?.summary?.totalDrivers || 0}
                  </p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">
                    Total Orders
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {data.driverBreakdown?.summary?.totalOrders || 0}
                  </p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">
                    Total Amount
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(
                      data.driverBreakdown?.summary?.totalAmount || 0
                    )}
                  </p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">
                    Total Driver Tips
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(
                      data.driverBreakdown?.summary?.totalDriverTips || 0
                    )}
                  </p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">
                    Total Delivery Fees
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(
                      data.driverBreakdown?.summary?.totalDeliveryFees || 0
                    )}
                  </p>
                </div>
                <div className="p-4 bg-primary/10 rounded-lg">
                  <p className="text-sm font-medium mb-1">Amount Collected</p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(
                      data.driverBreakdown?.summary?.totalAmountCollected || 0
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Individual Driver Breakdown */}
            {hasDriverBreakdown && (
              <div className="space-y-8">
                {data.driverBreakdown?.breakdown?.map((driver, driverIndex) => (
                  <div key={driverIndex} className="print-avoid-break">
                    {driverIndex > 0 && (
                      <div className="border-t-2 border-gray-300 my-8"></div>
                    )}

                    {/* Driver Header */}
                    <div className="bg-primary/5 p-4 rounded-lg border-l-4 border-primary mb-4">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                          <h4 className="text-lg font-bold text-foreground">
                            {driver.driverName || 'Unknown Driver'}
                          </h4>
                          <div className="flex gap-4 text-sm text-muted-foreground mt-1">
                            {driver.driverPhone && (
                              <span>📞 {driver.driverPhone}</span>
                            )}
                            {driver.driverEmail && (
                              <span>✉️ {driver.driverEmail}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">
                              Total Orders
                            </p>
                            <p className="text-2xl font-bold text-foreground">
                              {driver.totalOrderCount || 0}
                            </p>
                          </div>
                          <button
                            onClick={() => exportSingleDriverToCSV(driver)}
                            className="flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors print:hidden"
                          >
                            <Download className="h-4 w-4" />
                            Export
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Driver Metrics */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                      <div className="p-4 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground mb-1">
                          Total Amount
                        </p>
                        <p className="text-xl font-bold text-foreground">
                          {formatCurrency(driver.totalAmount || 0)}
                        </p>
                      </div>
                      <div className="p-4 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground mb-1">
                          Driver Tips
                        </p>
                        <p className="text-xl font-bold text-foreground">
                          {formatCurrency(driver.totalDriverTips || 0)}
                        </p>
                      </div>
                      <div className="p-4 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground mb-1">
                          Delivery Fees
                        </p>
                        <p className="text-xl font-bold text-foreground">
                          {formatCurrency(driver.totalDeliveryFees || 0)}
                        </p>
                      </div>
                      <div className="p-4 bg-primary/10 rounded-lg">
                        <p className="text-sm font-medium mb-1">
                          Amount Collected
                        </p>
                        <p className="text-xl font-bold text-foreground">
                          {formatCurrency(driver.totalAmountCollected || 0)}
                        </p>
                      </div>
                      <div className="p-4 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground mb-1">
                          Avg per Order
                        </p>
                        <p className="text-xl font-bold text-foreground">
                          {formatCurrency(
                            driver.totalOrderCount > 0
                              ? driver.totalAmount / driver.totalOrderCount
                              : 0
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Payment Method Breakdown */}
                    {driver.paymentBreakdown && (
                      <div className="overflow-x-auto">
                        <h5 className="text-sm font-semibold mb-3 text-foreground">
                          Payment Method Breakdown
                        </h5>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="min-w-[200px]">
                                Payment Type
                              </TableHead>
                              <TableHead className="text-right min-w-[120px]">
                                Order Count
                              </TableHead>
                              <TableHead className="text-right min-w-[120px]">
                                Total Amount
                              </TableHead>
                              <TableHead className="text-right min-w-[120px]">
                                Driver Tip
                              </TableHead>
                              <TableHead className="text-right min-w-[120px]">
                                Delivery Fee
                              </TableHead>
                              <TableHead className="text-right min-w-[120px]">
                                Amount Collected
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {showTender('card') && (
                              <TableRow>
                                <TableCell className="font-medium">
                                  Card
                                </TableCell>
                                <TableCell className="text-right">
                                  {driver.paymentBreakdown.card?.orderCount ||
                                    0}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(
                                    driver.paymentBreakdown.card?.total || 0
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(
                                    driver.paymentBreakdown.card?.driverTip || 0
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(
                                    driver.paymentBreakdown.card?.deliveryFee ||
                                      0
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(
                                    driver.paymentBreakdown.card
                                      ?.amountCollected || 0
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                            {showTender('cash') && (
                              <TableRow>
                                <TableCell className="font-medium">
                                  Cash
                                </TableCell>
                                <TableCell className="text-right">
                                  {driver.paymentBreakdown.cash?.orderCount ||
                                    0}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(
                                    driver.paymentBreakdown.cash?.total || 0
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(
                                    driver.paymentBreakdown.cash?.driverTip || 0
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(
                                    driver.paymentBreakdown.cash?.deliveryFee ||
                                      0
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(
                                    driver.paymentBreakdown.cash
                                      ?.amountCollected || 0
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                            {showTender('other') && (
                              <TableRow>
                                <TableCell className="font-medium">
                                  Other
                                </TableCell>
                                <TableCell className="text-right">
                                  {driver.paymentBreakdown.other?.orderCount ||
                                    0}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(
                                    driver.paymentBreakdown.other?.total || 0
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(
                                    driver.paymentBreakdown.other?.driverTip ||
                                      0
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(
                                    driver.paymentBreakdown.other
                                      ?.deliveryFee || 0
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(
                                    driver.paymentBreakdown.other
                                      ?.amountCollected || 0
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                            <TableRow>
                              <TableCell className="font-medium">
                                Reward
                              </TableCell>
                              <TableCell className="text-right">
                                {driver.paymentBreakdown.reward?.orderCount ||
                                  0}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(
                                  driver.paymentBreakdown.reward?.total || 0
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(
                                  driver.paymentBreakdown.reward?.driverTip || 0
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(
                                  driver.paymentBreakdown.reward?.deliveryFee ||
                                    0
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(
                                  driver.paymentBreakdown.reward
                                    ?.amountCollected || 0
                                )}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="font-medium">
                                Unpaid
                              </TableCell>
                              <TableCell className="text-right">
                                {driver.paymentBreakdown.unpaid.orderCount}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(
                                  driver.paymentBreakdown.unpaid.total || 0
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(
                                  driver.paymentBreakdown.unpaid
                                    .unpaidDriverTip || 0
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(
                                  driver.paymentBreakdown.unpaid
                                    .unpaidDeliveryFee || 0
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(
                                  driver.paymentBreakdown.unpaid
                                    .amountCollected || 0
                                )}
                              </TableCell>
                            </TableRow>
                            <TableRow className="bg-primary/10">
                              <TableCell className="font-bold">TOTAL</TableCell>
                              <TableCell className="text-right font-bold">
                                {driver.totalOrderCount || 0}
                              </TableCell>
                              <TableCell className="text-right font-bold">
                                {formatCurrency(driver.totalAmount || 0)}
                              </TableCell>
                              <TableCell className="text-right font-bold">
                                {formatCurrency(driver.totalDriverTips || 0)}
                              </TableCell>
                              <TableCell className="text-right font-bold">
                                {formatCurrency(driver.totalDeliveryFees || 0)}
                              </TableCell>
                              <TableCell className="text-right font-bold">
                                {formatCurrency(
                                  driver.totalAmountCollected || 0
                                )}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </CollapsibleCard>
      )}

      {/* Detailed Transactions */}
      {/* {data.detailedReport?.transactions &&
        data.detailedReport.transactions.length > 0 && (
          <Card className="print-card">
            <CardHeader>
              <CardTitle>Detailed Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[120px]">Order #</TableHead>
                      <TableHead className="min-w-[150px]">Date</TableHead>
                      <TableHead className="min-w-[120px]">
                        Payment Method
                      </TableHead>
                      <TableHead className="text-right min-w-[100px]">
                        Subtotal
                      </TableHead>
                      <TableHead className="text-right min-w-[100px]">
                        Tax
                      </TableHead>
                      <TableHead className="text-right min-w-[100px]">
                        Tip
                      </TableHead>
                      <TableHead className="text-right min-w-[100px]">
                        Total
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.detailedReport.transactions.map(
                      (transaction, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">
                            {transaction.orderNumber || 'N/A'}
                          </TableCell>
                          <TableCell>
                            {transaction.transactionDate
                              ? formatDateTime(transaction.transactionDate)
                              : 'N/A'}
                          </TableCell>
                          <TableCell className="capitalize">
                            {transaction.paymentMethod || 'N/A'}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(transaction.subtotal || 0)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(transaction.taxAmount || 0)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(transaction.tipAmount || 0)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(transaction.totalAmount || 0)}
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )} */}

      {/* Total Profit Report - before Report Footer (inventory_store only) */}
      {data.totalProfitReport && isInventoryStore && (
        <>
          <CollapsibleCard className="print-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center">
                    <DollarSign className="text-primary mr-3 h-5 w-5" />
                    Total Profit Report
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Report Period: {reportPeriod}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2 print:hidden">
                  <button
                    onClick={exportTotalProfitReportToCSV}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    Export to CSV
                  </button>
                  {data.totalProfitReport.daily &&
                    data.totalProfitReport.daily.length > 0 && (
                      <button
                        onClick={() => setShowTotalProfitReportDetail(true)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary hover:text-primary/80 hover:bg-primary/10 rounded-lg transition-colors"
                      >
                        More Details
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Subtotal</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(
                      data.totalProfitReport.totalSubtotal ??
                        data.totalProfitReport.totalSalesValue
                    )}
                  </p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Cost of Goods Sold
                  </p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(data.totalProfitReport.costOfGoodsSold)}
                  </p>
                </div>
                <div className="p-4 bg-primary/10 rounded-lg">
                  <p className="text-sm font-medium">Gross Profit</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(data.totalProfitReport.grossProfit)}
                  </p>
                  {data.totalProfitReport.grossProfitPercent != null && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {data.totalProfitReport.grossProfitPercent.toFixed(2)}%
                    </p>
                  )}
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Total Sales Value
                  </p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(data.totalProfitReport.totalSalesValue)}
                  </p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Total Tax</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(data.totalProfitReport.totalTax)}
                  </p>
                </div>
              </div>
            </CardContent>
          </CollapsibleCard>

          {/* Total Profit Report - Daily breakdown popup (orders only in CSV) */}
          {showTotalProfitReportDetail &&
            data.totalProfitReport.daily &&
            data.totalProfitReport.daily.length > 0 && (
              <div
                style={{ zIndex: 99999 }}
                className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
                onClick={() => setShowTotalProfitReportDetail(false)}
              >
                <div
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full overflow-hidden max-w-5xl flex flex-col"
                  style={{ maxHeight: '90vh' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Modal Header */}
                  <div className="flex items-center justify-between px-6 py-4 border-b bg-white dark:bg-gray-800 flex-shrink-0">
                    <h2 className="text-xl font-bold">
                      Total Profit Report – Daily Breakdown
                    </h2>
                    <button
                      onClick={() => setShowTotalProfitReportDetail(false)}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                  {/* Modal Content */}
                  <div className="p-6 overflow-auto flex-1">
                    <h3 className="text-lg font-bold mb-4">Daily Breakdown</h3>
                    <div className="overflow-x-auto">
                      <table
                        className="border-collapse w-full"
                        style={{ minWidth: 'max-content' }}
                      >
                        <colgroup>
                          <col style={{ width: '11rem', minWidth: '11rem' }} />
                        </colgroup>
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-3 font-medium whitespace-nowrap sticky left-0 bg-white dark:bg-gray-800 z-[100] border-r border-border">
                              Metric
                            </th>
                            <th className="text-right p-3 font-bold whitespace-nowrap">
                              Total
                            </th>
                            {data.totalProfitReport.daily.map((day, index) => (
                              <th
                                key={index}
                                className="text-right p-3 font-medium whitespace-nowrap"
                              >
                                {formatDate(day.date)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b">
                            <td className="p-3 font-medium whitespace-nowrap sticky left-0 bg-white dark:bg-gray-800 z-[100] border-r border-border">
                              Subtotal
                            </td>
                            <td className="text-right p-3 font-bold tabular-nums whitespace-nowrap">
                              {formatCurrency(
                                data.totalProfitReport.totalSubtotal ??
                                  data.totalProfitReport.totalSalesValue
                              )}
                            </td>
                            {data.totalProfitReport.daily.map((day, index) => (
                              <td
                                key={index}
                                className="text-right p-3 tabular-nums whitespace-nowrap"
                              >
                                {formatCurrency(
                                  day.subtotal ?? day.totalSalesValue
                                )}
                              </td>
                            ))}
                          </tr>
                          <tr className="border-b">
                            <td className="p-3 font-medium whitespace-nowrap sticky left-0 bg-white dark:bg-gray-800 z-[100] border-r border-border">
                              Cost of Goods Sold
                            </td>
                            <td className="text-right p-3 font-bold tabular-nums whitespace-nowrap">
                              {formatCurrency(
                                data.totalProfitReport.costOfGoodsSold
                              )}
                            </td>
                            {data.totalProfitReport.daily.map((day, index) => (
                              <td
                                key={index}
                                className="text-right p-3 tabular-nums whitespace-nowrap"
                              >
                                {formatCurrency(day.costOfGoodsSold)}
                              </td>
                            ))}
                          </tr>
                          <tr className="border-b">
                            <td className="p-3 font-bold whitespace-nowrap sticky left-0 bg-white dark:bg-gray-800 z-[100] border-r border-border">
                              Gross Profit
                            </td>
                            <td className="text-right p-3 font-bold tabular-nums whitespace-nowrap">
                              {formatCurrency(
                                data.totalProfitReport.grossProfit
                              )}
                              {data.totalProfitReport.grossProfitPercent !=
                                null && (
                                <span className="block text-xs text-muted-foreground">
                                  {data.totalProfitReport.grossProfitPercent.toFixed(
                                    2
                                  )}
                                  %
                                </span>
                              )}
                            </td>
                            {data.totalProfitReport.daily.map((day, index) => {
                              const grossProfit =
                                day.grossProfit ??
                                day.totalSalesValue - day.costOfGoodsSold;
                              return (
                                <td
                                  key={index}
                                  className="text-right p-3 font-bold tabular-nums whitespace-nowrap"
                                >
                                  {formatCurrency(grossProfit)}
                                  {day.grossProfitPercent != null && (
                                    <span className="block text-xs text-muted-foreground">
                                      {day.grossProfitPercent.toFixed(2)}%
                                    </span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                          <tr className="border-b">
                            <td className="p-3 font-medium whitespace-nowrap sticky left-0 bg-white dark:bg-gray-800 z-[100] border-r border-border">
                              Total Sales Value
                            </td>
                            <td className="text-right p-3 font-bold tabular-nums whitespace-nowrap">
                              {formatCurrency(
                                data.totalProfitReport.totalSalesValue
                              )}
                            </td>
                            {data.totalProfitReport.daily.map((day, index) => (
                              <td
                                key={index}
                                className="text-right p-3 tabular-nums whitespace-nowrap"
                              >
                                {formatCurrency(day.totalSalesValue)}
                              </td>
                            ))}
                          </tr>
                          <tr className="border-b">
                            <td className="p-3 font-medium whitespace-nowrap sticky left-0 bg-white dark:bg-gray-800 z-[100] border-r border-border">
                              Total Tax
                            </td>
                            <td className="text-right p-3 font-bold tabular-nums whitespace-nowrap">
                              {formatCurrency(data.totalProfitReport.totalTax)}
                            </td>
                            {data.totalProfitReport.daily.map((day, index) => (
                              <td
                                key={index}
                                className="text-right p-3 tabular-nums whitespace-nowrap"
                              >
                                {formatCurrency(day.totalTax)}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {/* Modal Footer */}
                  <div className="px-6 py-4 border-t flex justify-end bg-white dark:bg-gray-800 flex-shrink-0">
                    <button
                      onClick={() => setShowTotalProfitReportDetail(false)}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
        </>
      )}

      {/* Report Footer */}
      <Card className="print-card">
        <CardContent className="pt-6">
          <div className="border-t border-border pt-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between text-xs text-muted-foreground">
              <div>
                <p>
                  <strong>Report Generated By:</strong> POS Orders Analytics
                  Platform
                </p>
                <p>
                  <strong>Data Source:</strong> api.pos.orders.co
                </p>
              </div>
              <div className="mt-2 md:mt-0">
                <p>
                  <strong>Export Time:</strong> {formatDateTime()}
                </p>
                <p>
                  <strong>Data Freshness:</strong> Real-time
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employee Tip Details Modal */}
      {selectedEmployee && (
        <div
          style={{ zIndex: 99999 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setSelectedEmployee(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full overflow-hidden max-w-5xl flex flex-col"
            style={{ maxHeight: '90vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-white dark:bg-gray-800 flex-shrink-0">
              <div>
                <h2 className="text-xl font-bold">
                  {selectedEmployee.employeeFirstName}{' '}
                  {selectedEmployee.employeeLastName} - Detailed Breakdown
                </h2>
              </div>
              <button
                onClick={() => setSelectedEmployee(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-auto flex-1">
              {/* Performance Metrics - Daily Breakdown */}
              {selectedEmployee.dailyBreakdown &&
                (() => {
                  const activeDays = selectedEmployee.dailyBreakdown || [];

                  if (activeDays.length === 0) return null;

                  // Calculate totals
                  const totals = activeDays.reduce(
                    (acc, day) => ({
                      orders: acc.orders + (day.todaysTotalOrders || 0),
                      subtotal: acc.subtotal + (day.subtotal || 0),
                      tax: acc.tax + (day.tax || 0),
                      amountCollected:
                        acc.amountCollected + (day.amountCollected || 0),
                      adjustmentsAndFees:
                        acc.adjustmentsAndFees + (day.adjustmentsAndFees || 0),
                    }),
                    {
                      orders: 0,
                      subtotal: 0,
                      tax: 0,
                      amountCollected: 0,
                      adjustmentsAndFees: 0,
                    }
                  );

                  // Count transactions per day
                  const transactionsByDate = {};
                  if (selectedEmployee.detailedReport?.transactions) {
                    selectedEmployee.detailedReport.transactions.forEach(
                      (txn) => {
                        const txnDate = new Date(txn.createdAt)
                          .toISOString()
                          .split('T')[0];
                        transactionsByDate[txnDate] =
                          (transactionsByDate[txnDate] || 0) + 1;
                      }
                    );
                  }

                  const totalTransactions = Object.values(
                    transactionsByDate
                  ).reduce((sum, count) => sum + count, 0);

                  return (
                    <div className="mb-8">
                      <h3 className="text-lg font-bold mb-4">
                        Daily Breakdown
                      </h3>
                      <div className="overflow-x-auto min-w-full">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-3 font-medium min-w-[200px]">
                                Metric
                              </th>
                              <th className="text-right p-3 font-bold min-w-[120px]">
                                Total
                              </th>
                              {activeDays.map((day, index) => (
                                <th
                                  key={index}
                                  className="text-right p-3 font-medium min-w-[120px]"
                                >
                                  {formatDate(day.date)}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b">
                              <td className="p-3 font-medium">Orders</td>
                              <td className="text-right p-3 font-bold">
                                {totals.orders}
                              </td>
                              {activeDays.map((day, index) => (
                                <td key={index} className="text-right p-3">
                                  {day.todaysTotalOrders || 0}
                                </td>
                              ))}
                            </tr>
                            <tr className="border-b">
                              <td className="p-3 font-medium">Subtotal</td>
                              <td className="text-right p-3 font-bold">
                                {formatCurrency(totals.subtotal)}
                              </td>
                              {activeDays.map((day, index) => (
                                <td key={index} className="text-right p-3">
                                  {formatCurrency(day.subtotal || 0)}
                                </td>
                              ))}
                            </tr>
                            <tr className="border-b">
                              <td className="p-3 font-medium">Tax</td>
                              <td className="text-right p-3 font-bold">
                                {formatCurrency(totals.tax)}
                              </td>
                              {activeDays.map((day, index) => (
                                <td key={index} className="text-right p-3">
                                  {formatCurrency(day.tax || 0)}
                                </td>
                              ))}
                            </tr>
                            <tr className="border-b">
                              <td className="p-3 font-medium">
                                Adjustments & Fees
                              </td>
                              <td className="text-right p-3 font-bold">
                                {formatCurrency(totals.adjustmentsAndFees)}
                              </td>
                              {activeDays.map((day, index) => (
                                <td key={index} className="text-right p-3">
                                  {formatCurrency(day.adjustmentsAndFees || 0)}
                                </td>
                              ))}
                            </tr>
                            {data.amountCollected != null && (
                              <tr className="border-b">
                                <td className="p-3 font-medium">
                                  Amount Collected
                                </td>
                                <td className="text-right p-3 font-bold">
                                  {formatCurrency(totals.amountCollected)}
                                </td>
                                {activeDays.map((day, index) => (
                                  <td key={index} className="text-right p-3">
                                    {formatCurrency(day.amountCollected || 0)}
                                  </td>
                                ))}
                              </tr>
                            )}
                            <tr className="border-b">
                              <td className="p-3 font-medium">
                                Number of Payments
                              </td>
                              <td className="text-right p-3 font-bold">
                                {totalTransactions}
                              </td>
                              {activeDays.map((day, index) => (
                                <td key={index} className="text-right p-3">
                                  {transactionsByDate[day.date] || 0}
                                </td>
                              ))}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

              {/* Tip Breakdown - Daily Breakdown */}
              {selectedEmployee.kitchenTipBreakdown?.daily &&
                (() => {
                  const activeTipDays =
                    selectedEmployee.kitchenTipBreakdown.daily || [];

                  return (
                    <div className="mt-8">
                      <h3 className="text-lg font-bold mb-4">Tip Breakdown</h3>
                      <div className="overflow-x-auto">
                        <div className="min-w-full inline-block">
                          <table className="w-full border-collapse">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left p-3 font-medium min-w-[200px]">
                                  Metric
                                </th>
                                <th className="text-right p-3 font-bold min-w-[120px]">
                                  Total
                                </th>
                                {activeTipDays.map((day, index) => (
                                  <th
                                    key={index}
                                    className="text-right p-3 font-medium min-w-[120px]"
                                  >
                                    {formatDate(day.date)}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-b">
                                <td className="p-3 font-medium">
                                  Non-cash Kitchen Tip Paid
                                </td>
                                <td className="text-right p-3 font-bold">
                                  {formatCurrency(
                                    selectedEmployee.kitchenTipBreakdown
                                      .kitchenTipPaidNonCash || 0
                                  )}
                                </td>
                                {activeTipDays.map((day, index) => (
                                  <td key={index} className="text-right p-3">
                                    {formatCurrency(day.purchaseNonCash || 0)}
                                  </td>
                                ))}
                              </tr>
                              <tr className="border-b">
                                <td className="p-3 font-medium">
                                  Non-cash Kitchen Tip Refunded
                                </td>
                                <td className="text-right p-3 font-bold">
                                  {formatCurrency(
                                    -Math.abs(
                                      selectedEmployee.kitchenTipBreakdown
                                        .kitchenTipRefundedNonCash || 0
                                    )
                                  )}
                                </td>
                                {activeTipDays.map((day, index) => (
                                  <td key={index} className="text-right p-3">
                                    {formatCurrency(
                                      -Math.abs(day.refundNonCash || 0)
                                    )}
                                  </td>
                                ))}
                              </tr>
                              <tr className="bg-primary/10 border-b">
                                <td className="p-3 font-bold">
                                  Non-cash Kitchen Tip Collected
                                </td>
                                <td className="text-right p-3 font-bold">
                                  {formatCurrency(
                                    selectedEmployee.kitchenTipBreakdown
                                      .kitchenTipCollectedNonCash || 0
                                  )}
                                </td>
                                {activeTipDays.map((day, index) => (
                                  <td
                                    key={index}
                                    className="text-right p-3 font-bold"
                                  >
                                    {formatCurrency(
                                      (day.purchaseNonCash || 0) +
                                        (day.refundNonCash || 0)
                                    )}
                                  </td>
                                ))}
                              </tr>
                              <tr className="border-b">
                                <td className="p-3 font-medium">
                                  Cash Kitchen Tip Paid
                                </td>
                                <td className="text-right p-3 font-bold">
                                  {formatCurrency(
                                    selectedEmployee.kitchenTipBreakdown
                                      .kitchenTipPaidCash || 0
                                  )}
                                </td>
                                {activeTipDays.map((day, index) => (
                                  <td key={index} className="text-right p-3">
                                    {formatCurrency(day.purchaseCash || 0)}
                                  </td>
                                ))}
                              </tr>
                              <tr className="border-b">
                                <td className="p-3 font-medium">
                                  Cash Kitchen Tip Refunded
                                </td>
                                <td className="text-right p-3 font-bold">
                                  {formatCurrency(
                                    -Math.abs(
                                      selectedEmployee.kitchenTipBreakdown
                                        .kitchenTipRefundedCash || 0
                                    )
                                  )}
                                </td>
                                {activeTipDays.map((day, index) => (
                                  <td key={index} className="text-right p-3">
                                    {formatCurrency(
                                      -Math.abs(day.refundCash || 0)
                                    )}
                                  </td>
                                ))}
                              </tr>
                              <tr className="bg-primary/10 border-b">
                                <td className="p-3 font-bold">
                                  Cash Kitchen Tip Collected
                                </td>
                                <td className="text-right p-3 font-bold">
                                  {formatCurrency(
                                    selectedEmployee.kitchenTipBreakdown
                                      .kitchenTipCollectedCash || 0
                                  )}
                                </td>
                                {activeTipDays.map((day, index) => (
                                  <td
                                    key={index}
                                    className="text-right p-3 font-bold"
                                  >
                                    {formatCurrency(
                                      (day.purchaseCash || 0) +
                                        (day.refundCash || 0)
                                    )}
                                  </td>
                                ))}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                })()}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t flex justify-end bg-white dark:bg-gray-800 flex-shrink-0">
              <button
                onClick={() => setSelectedEmployee(null)}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </CollapsibleCardGroup>
  );
}
